import get from "lodash/get";
import groupBy from "lodash/groupBy";
import {
  Features,
  PlanFeatures,
  channelToAddonLimitsName,
} from "../../util/constants";
import * as Sentry from "@sentry/browser";

const SHARED_LIMITS_FEATURES = {
  [Features.TEMPLATES]: Object.values(channelToAddonLimitsName),
};

const isSharedLimitFeature = (featureName) =>
  featureName in SHARED_LIMITS_FEATURES;

function isPrimitive(val) {
  return val == null || /^[sbn]/.test(typeof val);
}

const matchers = {
  organisation: {
    accountingSoftware: (v, { organisation }) =>
      v === organisation.accountingSoftware,
    shortCode: (v, { organisation }) => v === organisation.shortCode,
    chaserCore: (v, { organisation }) => v === organisation.chaserCore,
    countryCode: (v, { organisation }) => v === organisation.countryCode,
    currencyCode: (v, { organisation }) => v === organisation.currencyCode,
    createdAt: (v, { organisation }) => v === organisation.createdAt,
    createdBefore: (v, { organisation }) =>
      new Date(v) > new Date(organisation.createdAt),
    createdAfter: (v, { organisation }) =>
      new Date(v) < new Date(organisation.createdAt),
  },
  subscription: {
    subscriptionService: (v, { subscription }) =>
      v === subscription.subscriptionService ||
      !subscription.subscriptionService,
    planName: (v, { subscription }) => v === subscription.planName,
    plan: (v, { subscription }) => v === subscription.planName,
    isExpiredTrial: (v, { subscription }) => v === subscription.isExpiredTrial,
    isInTrial: (v, { subscription }) => v === subscription.isInTrial,
    isSubscribed: (v, { subscription }) => v === subscription.isSubscribed,
  },
  user: {
    id: (v, { user }) => v === user.id,
    role: (v, { user }) => user.permissions?.global?.some((r) => v === r),
  },
};

function test(config, tests, path = []) {
  if (!tests) return true;
  if (isPrimitive(tests)) {
    const theTest = get(matchers, path);
    if (!theTest) return true;
    return theTest(tests, config);
  }
  if (Array.isArray(tests)) {
    return tests.some((val) => {
      return test(config, val, [...path]);
    });
  }
  return Object.keys(tests).every((objKey) => {
    const val = tests[objKey];
    const p = [...path];
    if (objKey !== "$or") p.push(objKey);
    return test(config, val, p);
  });
}

function parseFeature(feature, name, user, organisation, subscription) {
  if (!feature) return { enabled: false };

  const isPlanAllowed =
    !Array.isArray(feature.plans) || feature.plans.length === 0
      ? true
      : feature.plans.includes(subscription?.planName);

  return {
    enabled:
      feature.enabled !== false &&
      test({ name, user, organisation, subscription }, feature.segment) &&
      isPlanAllowed,
    beta: feature.beta ?? false,
    promote: feature.promote ?? false,
    isAvailableToTrial: feature.isAvailableToTrial ?? true,
  };
}

const planFeatureHasAddons = ({ featureName }) => {
  const FEATURES_WITH_ADDONS = [
    PlanFeatures.TFP_EMAILS,
    PlanFeatures.SCHEDULES,
    PlanFeatures.TEMPLATES,
    PlanFeatures.USERS,
    PlanFeatures.EMAIL_INTEGRATION_CONNECTIONS,
    Features.SMS,
    Features.PHONE_CALL,
    Features.LETTER,
  ];

  return FEATURES_WITH_ADDONS.includes(featureName);
};

function extractSubscriptionState(org) {
  const { subscriptionService, isExpiredTrial, isInTrial, isSubscribed } =
    org?.subscription || {};
  return { subscriptionService, isExpiredTrial, isInTrial, isSubscribed };
}

function toUserContext(user) {
  return {
    id: user.userId,
    permissions: user.permissions,
  };
}

function toOrganisationContext(org) {
  return {
    shortCode: org?.ShortCode,
    countryCode: org?.CountryCode,
    currencyCode: org?.BaseCurrency,
    createdAt: org?.createdAt,
    accountingSoftware: org?.accountingSoftware,
    chaserCore: org?.features?.chaser_core,
  };
}

function toSubscriptionContext(plan, subscriptionState) {
  return {
    planName: plan?.name,
    subscriptionService: subscriptionState.subscriptionService,
    isExpiredTrial: subscriptionState.isExpiredTrial,
    isInTrial: subscriptionState.isInTrial,
    isSubscribed: subscriptionState.isSubscribed,
  };
}

function evaluateFeatureFlagConfig(
  featureConfig,
  featureName,
  user,
  org,
  plan,
  subscriptionState,
) {
  if (!featureConfig) {
    const isKnownFeature =
      Object.values(Features).includes(featureName) ||
      Object.values(PlanFeatures).includes(featureName);
    return {
      enabled: isKnownFeature,
      isAvailableToTrial: true,
    };
  }
  return parseFeature(
    featureConfig,
    featureName,
    toUserContext(user),
    toOrganisationContext(org),
    toSubscriptionContext(plan, subscriptionState),
  );
}

function isAPlanRestrictedFeature(featureName) {
  return Object.values(PlanFeatures).includes(featureName);
}

function checkIfAvailableToTrial(featureFlagConfig) {
  return Boolean(featureFlagConfig.isAvailableToTrial);
}

function canOrgViewFeature(
  featureFlagConfig,
  featureName,
  featureVariant,
  plan = {},
  hasAddons = false,
  isInTrial = false,
  subscriptionFeatures = {},
) {
  if (featureFlagConfig.beta) return true;
  if (isInTrial) return checkIfAvailableToTrial(featureFlagConfig);
  if (!isAPlanRestrictedFeature(featureName)) return true;
  if (hasAddons) {
    if (isSharedLimitFeature(featureName) && featureVariant) {
      return (
        plan.addons?.[featureName]?.sharedLimits?.[featureVariant]?.canAccess ??
        false
      );
    } else {
      return plan.addons?.[featureName]?.canAccess ?? false;
    }
  }

  return subscriptionFeatures?.[featureName] ?? false;
}

function getPlansOfferingFeature(allPlans, featureName, hasAddons, plan) {
  return Object.entries(allPlans)
    .filter(([, obj]) =>
      hasAddons
        ? obj.features[featureName] >
            (plan?.addons?.[featureName]?.planLimit || 0) ||
          obj.features[featureName] === true
        : obj.features[featureName] === true,
    )
    .map(([key]) => key);
}

function getFeature(
  firebaseFeatures,
  featureName,
  user,
  org,
  allPlans = {},
  planProp,
  featureVariant,
  subscription,
) {
  if (!subscription) {
    Sentry.captureMessage("Missing subscription for feature gating");
  }

  const hasAddons = planFeatureHasAddons({ featureName });
  const subscriptionState = extractSubscriptionState(org);
  const subscriptionFeatures = subscription?.features;
  const featureConfig = firebaseFeatures[featureName];
  const plan = planProp || org?.subscription?.plan;

  const featureFlagConfig = evaluateFeatureFlagConfig(
    featureConfig,
    featureName,
    user,
    org,
    plan,
    subscriptionState,
  );

  const hasAccessToFeatureOnSubscription = canOrgViewFeature(
    featureFlagConfig,
    featureName,
    featureVariant,
    plan,
    hasAddons,
    subscriptionState.isInTrial,
    subscriptionFeatures,
  );

  const plans = getPlansOfferingFeature(allPlans, featureName, hasAddons, plan);

  return {
    name: featureName,
    enabled: featureFlagConfig?.enabled,
    beta: featureFlagConfig?.beta,
    promote: featureFlagConfig?.promote,
    plans,
    canAccess: hasAccessToFeatureOnSubscription,
    hasAddons,
  };
}

export { parseFeature, groupBy, getFeature };
